import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Calculator, RefreshCw, AlertTriangle,
         CheckCircle2, Shield, BookMarked, Hash, Printer } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ExecutiveInsights from "@/components/ExecutiveInsights";
import BaselineSummary from "@/components/BaselineSummary";
import NigeriaEnergyModule from "@/components/NigeriaEnergyModule";
import IntelligencePanel from "@/components/IntelligencePanel";
import InvestmentPackPanel from "@/components/InvestmentPackPanel";
import VerificationCard from "@/components/VerificationCard";
import FinancingReadinessScore from "@/components/FinancingReadinessScore";
import VerificationFeePanel from "@/components/VerificationFeePanel";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const CATEGORIES = ["electricity", "fuel", "materials", "waste", "transport", "water", "production"];
const UNITS_BY_CATEGORY = {
  electricity: ["kWh", "MWh", "GJ"], fuel: ["liter", "m3", "GJ", "kg"],
  materials: ["kg", "tonne", "unit"], waste: ["kg", "tonne", "m3"],
  transport: ["km", "tonne-km"], water: ["m3", "liter"], production: ["unit", "kg", "tonne"]
};

const STATUS_CONFIG = {
  draft: "bg-slate-100 text-slate-600",
  data_collection: "bg-blue-50 text-blue-700",
  analysis: "bg-indigo-50 text-indigo-700",
  review: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  issued: "bg-green-50 text-green-800",
  archived: "bg-slate-50 text-slate-400"
};

const CONF_CONFIG = {
  high: { label: "High Confidence", class: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  medium: { label: "Medium Confidence", class: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  low: { label: "Low Confidence", class: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  very_low: { label: "Very Low Confidence", class: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" }
};

const METHODOLOGY_ASSUMPTIONS = [
  "Activity data represents the complete reporting period (annual unless stated).",
  "All grid electricity consumption uses country-specific grid emission factors where available.",
  "Transport emissions use the distance-based calculation method.",
  "Waste emission factors assume average mixed waste composition.",
  "No double-counting between GHG scopes.",
  "All factors are approved for the reporting period and geography."
];

export default function AssessmentWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [activities, setActivities] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [scorecard, setScorecard] = useState(null);
  const [results, setResults] = useState([]);
  const [methodology, setMethodology] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [activityForm, setActivityForm] = useState({
    category: "electricity", description: "", quantity: "", unit: "kWh",
    scope: 1, source: "", period_start: "", period_end: ""
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, actRes, evRes] = await Promise.all([
        client.get(`/assessments/${id}`),
        client.get(`/assessments/${id}/activities`),
        client.get(`/evidence?assessment_id=${id}`)
      ]);
      setAssessment(aRes.data);
      setActivities(actRes.data);
      setEvidence(evRes.data);
      if (aRes.data.methodology_id) {
        try {
          const mRes = await client.get(`/methodologies/${aRes.data.methodology_id}`);
          setMethodology(mRes.data);
        } catch {}
      }
      try {
        const compRes = await client.get(`/assessments/${id}/completeness`);
        setCompleteness(compRes.data);
      } catch {}
      try {
        const sRes = await client.get(`/assessments/${id}/scorecard`);
        setScorecard(sRes.data);
        const rRes = await client.get(`/assessments/${id}/results`);
        setResults(rRes.data.line_items || []);
      } catch {}
    } catch { toast.error("Failed to load assessment"); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      await client.post(`/assessments/${id}/activities`, {
        ...activityForm, quantity: parseFloat(activityForm.quantity), scope: parseInt(activityForm.scope)
      });
      toast.success("Activity record added");
      setShowAddActivity(false);
      setActivityForm({ category: "electricity", description: "", quantity: "", unit: "kWh", scope: 1, source: "", period_start: "", period_end: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add activity"); }
  };

  const handleDeleteActivity = async (actId) => {
    if (!window.confirm("Delete this activity record?")) return;
    try { await client.delete(`/assessments/${id}/activities/${actId}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const res = await client.post(`/assessments/${id}/calculate`);
      toast.success(`Calculations complete! ${res.data.total_emissions_tco2e?.toFixed(2)} tCO₂e | Confidence: ${res.data.confidence_classification?.toUpperCase()}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Calculation failed"); }
    setCalculating(false);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await client.patch(`/assessments/${id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Status update failed"); }
  };

  const loadAiInsights = async (type) => {
    setAiLoading(true);
    try {
      const res = await client.post("/ai/insights", { assessment_id: parseInt(id), insight_type: type });
      setAiInsights(res.data);
    } catch { toast.error("AI insights unavailable"); }
    setAiLoading(false);
  };

  if (loading) return <div className="p-8"><div className="h-6 w-48 bg-slate-200 rounded animate-pulse mb-4" /><div className="h-64 bg-white border border-slate-200 rounded-xl animate-pulse" /></div>;
  if (!assessment) return <div className="p-8 text-center"><p className="text-slate-500">Assessment not found.</p><button onClick={() => navigate("/assessments")} className="mt-3 text-[#059669] text-sm hover:underline">Back</button></div>;

  const confConfig = CONF_CONFIG[scorecard?.confidence_classification] || CONF_CONFIG.medium;

  return (
    <div className="flex flex-col h-full animate-fade-in">
      {/* Header */}
      <div className="px-6 lg:px-8 pt-6 pb-0 bg-white border-b border-slate-200">
        <button onClick={() => navigate("/assessments")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors">
          <ArrowLeft size={14} /> Back to Assessments
        </button>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-1.5 flex-wrap">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[assessment.status] || "bg-slate-100 text-slate-600"}`} data-testid="assessment-status">
                {assessment.status?.replace(/_/g, " ")}
              </span>
              {scorecard?.confidence_classification && (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${confConfig.class}`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${confConfig.dot}`} />
                  {confConfig.label}
                </span>
              )}
              {assessment.reporting_year && <span className="text-xs text-slate-400">FY{assessment.reporting_year}</span>}
              {assessment.geography && <span className="text-xs text-slate-400 uppercase">{assessment.geography}</span>}
            </div>
            
      <PageHeader
        title={assessment.title}
        backUrl="/assessments"
        backLabel="Back to Assessments"
      />
          </div>
          <div className="flex items-center gap-2 ml-4">
            {assessment.status === "draft" && (
              <button onClick={() => handleStatusChange("data_collection")} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700">Start Data Collection</button>
            )}
            {assessment.status === "analysis" && (
              <button onClick={() => handleStatusChange("review")} className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700">Submit for Review</button>
            )}
            {assessment.status === "review" && (
              <button onClick={() => handleStatusChange("approved")} className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700">Approve</button>
            )}
            {assessment.status === "approved" && (
              <button onClick={() => handleStatusChange("issued")} className="px-3 py-1.5 bg-[#0F172A] text-white text-xs font-medium rounded-lg hover:bg-[#1E293B]">Issue</button>
            )}
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          {/* Completeness warnings */}
          {completeness && completeness.issues && completeness.issues.length > 0 && (
            <div className="mb-3 space-y-1">
              {completeness.issues.map((issue, i) => (
                <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${issue.severity === "error" ? "bg-red-50 border border-red-200 text-red-700" : "bg-amber-50 border border-amber-200 text-amber-700"}`}>
                  <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{issue.message}</span>
                </div>
              ))}
            </div>
          )}
          <TabsList className="bg-transparent border-0 h-auto p-0 gap-0 w-full overflow-x-auto">
            {[["overview","Overview"], ["methodology","Methodology"], ["activity-data","Activity Data"], ["evidence","Evidence"], ["calculations","Calculations"], ["results","Results"], ["baseline","Baseline"], ["energy-mix","Energy Mix"], ["intelligence","Intelligence"], ["investment-pack","Investment Pack"], ["reports","Reports"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#059669] data-[state=active]:text-[#059669] data-[state=active]:bg-transparent px-4 pb-3 text-sm font-medium text-slate-500 hover:text-slate-800 whitespace-nowrap">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="bg-[#F8FAFC] p-6 lg:p-8 overflow-auto" style={{ maxHeight: "calc(100vh - 270px)" }}>

            {/* OVERVIEW */}
            <TabsContent value="overview" className="mt-0 space-y-4">
              {/* Trust layer — always visible */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <VerificationCard assessmentId={parseInt(id)} />
                <FinancingReadinessScore assessmentId={parseInt(id)} mode="panel" />
              </div>

              {/* Third-party verification fee — shows when pending client approval */}
              <VerificationFeePanel assessmentId={parseInt(id)} />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Activity Records", value: activities.length },
                  { label: "Evidence Files", value: evidence.length },
                  { label: "Overall Score", value: scorecard ? `${scorecard.overall_score}/100` : "—" },
                  { label: "Confidence", value: scorecard?.confidence_classification ? scorecard.confidence_classification.toUpperCase() : "—" }
                ].map(({ label, value }) => (
                  <MetricCard
                      label={label}
                      value={value}
                      color="text-slate-900"
                    />
                ))}
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold mb-4">Scope & Boundary Settings</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[1,2,3].map(s => (
                    <div key={s} className={`p-4 rounded-lg border ${assessment[`scope_${s}`] ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-5 h-5 rounded flex items-center justify-center ${assessment[`scope_${s}`] ? "bg-emerald-600" : "bg-slate-300"}`}>
                          {assessment[`scope_${s}`] && <CheckCircle2 size={12} className="text-white" />}
                        </div>
                        <span className="text-sm font-medium">Scope {s}</span>
                      </div>
                      <p className="text-xs text-slate-500">{s === 1 ? "Direct emissions" : s === 2 ? "Purchased energy" : "Value chain"}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* METHODOLOGY PANEL */}
            <TabsContent value="methodology" className="mt-0 space-y-4">
              {methodology ? (
                <>
                  {/* Methodology header */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <BookMarked size={18} className="text-[#059669]" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-slate-900">{methodology.name}</h3>
                          <p className="text-sm text-slate-500 mt-0.5">Version {methodology.version} · {methodology.standard}</p>
                        </div>
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${methodology.status === "approved_active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {methodology.status === "approved_active" ? "Approved Active" : methodology.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{methodology.description}</p>
                  </div>

                  {/* Boundary rules */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-slate-900 mb-4">Boundary & Scope Rules</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      {[1, 2, 3].map(scope => (
                        <div key={scope} className={`p-4 rounded-xl border ${assessment[`scope_${scope}`] ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200 opacity-60"}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${assessment[`scope_${scope}`] ? "bg-emerald-600 text-white" : "bg-slate-300 text-white"}`}>
                              {scope}
                            </div>
                            <span className="text-xs font-semibold text-slate-900">
                              {scope === 1 ? "Direct Emissions" : scope === 2 ? "Indirect (Energy)" : "Value Chain"}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">
                            {scope === 1 && "Scope 1 covers direct GHG emissions from sources owned or controlled by the organization."}
                            {scope === 2 && "Scope 2 covers indirect emissions from the generation of purchased energy (electricity, heat, steam)."}
                            {scope === 3 && "Scope 3 covers all other indirect emissions in the value chain (upstream and downstream)."}
                          </p>
                          <div className="mt-2">
                            <span className={`text-xs font-semibold ${assessment[`scope_${scope}`] ? "text-emerald-700" : "text-slate-400"}`}>
                              {assessment[`scope_${scope}`] ? "INCLUDED" : "EXCLUDED"}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
                      {[
                        { label: "Scope Coverage", value: methodology.scope },
                        { label: "Geography", value: assessment.geography?.toUpperCase() || "Global" },
                        { label: "Reporting Year", value: assessment.reporting_year || "—" },
                        { label: "Applicability", value: methodology.applicability || "All sectors" },
                      ].map(({ label, value }) => (
                        <div key={label} className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                          <p className="text-slate-400 mb-0.5 uppercase tracking-wider">{label}</p>
                          <p className="font-medium text-slate-700">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Key assumptions */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-slate-900 mb-4">Key Assumptions</h3>
                    <div className="space-y-2">
                      {METHODOLOGY_ASSUMPTIONS.map((assumption, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 bg-slate-100 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-semibold text-slate-500">{i + 1}</span>
                          </div>
                          <p className="text-sm text-slate-600">{assumption}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Factor sources */}
                  {scorecard?.factor_set_snapshot?.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <h3 className="text-sm font-semibold text-slate-900">Emission Factor Sources Used</h3>
                        <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">{scorecard.factor_set_snapshot.length} factors</span>
                      </div>
                      <div className="space-y-2">
                        {[...new Set(scorecard.factor_set_snapshot.map(f => f.source))].filter(Boolean).map(source => {
                          const count = scorecard.factor_set_snapshot.filter(f => f.source === source).length;
                          return (
                            <div key={source} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg">
                              <span className="text-sm font-medium text-slate-900">{source}</span>
                              <span className="text-xs text-slate-400">{count} factor{count > 1 ? "s" : ""}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-400">
                  <BookMarked size={28} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No methodology assigned to this assessment.</p>
                  <p className="text-xs mt-1">Edit the assessment to assign a methodology.</p>
                </div>
              )}
            </TabsContent>

            {/* ACTIVITY DATA */}
            <TabsContent value="activity-data" className="mt-0 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">{activities.length} activity records</p>
                <div className="flex gap-2">
                  <button onClick={() => setShowAddActivity(!showAddActivity)} className="inline-flex items-center gap-2 px-3 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B]" data-testid="add-activity-btn">
                    <Plus size={13} /> Add Record
                  </button>
                  <button onClick={handleCalculate} disabled={calculating || activities.length === 0}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-[#059669] text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50" data-testid="calculate-btn">
                    {calculating ? <RefreshCw size={13} className="animate-spin" /> : <Calculator size={13} />}
                    {calculating ? "Calculating..." : "Calculate"}
                  </button>
                </div>
              </div>

              {showAddActivity && (
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold mb-4">Add Activity Record</h3>
                  <form onSubmit={handleAddActivity} className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Category *</label>
                      <select value={activityForm.category} onChange={e => setActivityForm(f => ({ ...f, category: e.target.value, unit: UNITS_BY_CATEGORY[e.target.value]?.[0] || "" }))} className="w-full h-9 px-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="activity-category-select">
                        {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Description</label>
                      <input type="text" value={activityForm.description} onChange={e => setActivityForm(f => ({ ...f, description: e.target.value }))} className="w-full h-9 px-2 border border-slate-300 rounded-lg text-sm" placeholder="e.g. Facility electricity" data-testid="activity-description-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Quantity *</label>
                      <input type="number" step="any" value={activityForm.quantity} onChange={e => setActivityForm(f => ({ ...f, quantity: e.target.value }))} required className="w-full h-9 px-2 border border-slate-300 rounded-lg text-sm" data-testid="activity-quantity-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Unit *</label>
                      <select value={activityForm.unit} onChange={e => setActivityForm(f => ({ ...f, unit: e.target.value }))} className="w-full h-9 px-2 border border-slate-300 rounded-lg text-sm bg-white">
                        {(UNITS_BY_CATEGORY[activityForm.category] || ["unit"]).map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Scope</label>
                      <select value={activityForm.scope} onChange={e => setActivityForm(f => ({ ...f, scope: e.target.value }))} className="w-full h-9 px-2 border border-slate-300 rounded-lg text-sm bg-white">
                        <option value={1}>Scope 1 — Direct</option>
                        <option value={2}>Scope 2 — Indirect</option>
                        <option value={3}>Scope 3 — Value Chain</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Data Source</label>
                      <input type="text" value={activityForm.source} onChange={e => setActivityForm(f => ({ ...f, source: e.target.value }))} className="w-full h-9 px-2 border border-slate-300 rounded-lg text-sm" placeholder="Invoice, meter reading..." />
                    </div>
                    <div className="col-span-2 sm:col-span-3 flex gap-2 justify-end">
                      <button type="button" onClick={() => setShowAddActivity(false)} className="px-3 py-1.5 border border-slate-300 text-sm rounded-lg hover:bg-slate-50">Cancel</button>
                      <button type="submit" className="px-4 py-1.5 bg-[#0F172A] text-white text-sm rounded-lg hover:bg-[#1E293B]" data-testid="save-activity-btn">Add Record</button>
                    </div>
                  </form>
                </div>
              )}

              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Category</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden sm:table-cell">Description</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Quantity</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Unit</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Scope</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {activities.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">No activity records. Add data to begin the assessment.</td></tr>
                    ) : activities.map(act => (
                      <tr key={act.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3"><span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded capitalize">{act.category}</span></td>
                        <td className="px-4 py-3 text-sm text-slate-600 hidden sm:table-cell">{act.description || "—"}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium tabular-nums">{act.quantity?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{act.unit}</td>
                        <td className="px-4 py-3 text-xs text-slate-400 hidden md:table-cell">Scope {act.scope}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => handleDeleteActivity(act.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* EVIDENCE */}
            <TabsContent value="evidence" className="mt-0">
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold">Linked Evidence ({evidence.length})</h3>
                  <button onClick={() => navigate("/evidence")} className="text-xs text-[#059669] hover:underline">Go to Evidence Vault</button>
                </div>
                {evidence.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">No evidence linked. Upload in Evidence Vault and link to this assessment.</p>
                ) : evidence.map(e => (
                  <div key={e.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg mb-2">
                    <div><p className="text-sm font-medium">{e.name}</p><p className="text-xs text-slate-400 capitalize">{e.type?.replace(/_/g, " ")}</p></div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${e.status === "verified" ? "bg-emerald-50 text-emerald-700" : e.status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{e.status}</span>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* CALCULATIONS */}
            <TabsContent value="calculations" className="mt-0 space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-500">Calculations are traceable to approved factors with locked versions.</p>
                <button onClick={handleCalculate} disabled={calculating || activities.length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#059669] text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50" data-testid="run-calculations-btn">
                  {calculating ? <><RefreshCw size={13} className="animate-spin" /> Calculating...</> : <><Calculator size={13} /> Run Calculations</>}
                </button>
              </div>
              {activities.length === 0 && (
                <div className="flex gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-700">Add activity records before calculating.</p>
                </div>
              )}
              {results.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Calculation Results — Factor Provenance</p>
                    {scorecard?.calculation_hash && (
                      <div className="flex items-center gap-1.5 text-xs text-slate-400">
                        <Hash size={11} /><span className="font-mono">{scorecard.calculation_hash}</span>
                      </div>
                    )}
                  </div>
                  <table className="w-full">
                    <thead><tr className="border-b border-slate-100">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Category</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">Quantity</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 hidden md:table-cell">Factor (v{1})</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500">tCO₂e</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-50">
                      {results.map((r, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 text-sm capitalize">{r.category}</td>
                          <td className="px-4 py-2.5 text-sm text-right tabular-nums">{r.quantity_used?.toLocaleString()} {r.unit_used}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-400 hidden md:table-cell">{r.factor_value_used} {r.factor_unit}</td>
                          <td className="px-4 py-2.5 text-sm font-semibold text-right tabular-nums text-[#059669]">{r.emissions_tco2e?.toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            {/* RESULTS */}
            <TabsContent value="results" className="mt-0 space-y-4">
              {!scorecard ? (
                <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                  <Calculator size={28} className="mx-auto mb-3 text-slate-300" />
                  <p className="text-sm text-slate-500">Run calculations to generate scorecard.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: "Total Emissions", value: `${scorecard.total_emissions_tco2e?.toFixed(2)} tCO₂e`, color: "text-slate-900" },
                      { label: "Overall Score", value: `${scorecard.overall_score}/100`, color: "text-[#059669]" },
                      { label: "Data Completeness", value: `${scorecard.data_completeness}%`, color: "text-blue-600" },
                      { label: "Evidence Coverage", value: `${scorecard.evidence_coverage}%`, color: "text-amber-600" }
                    ].map(({ label, value, color }) => (
                      <MetricCard key={label} label={label} value={value} sub={unit} color={color} />
                    ))}
                  </div>

                  {/* Confidence breakdown */}
                  {scorecard.confidence_components && (
                    <div className="bg-white border border-slate-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-slate-900">Data Confidence Analysis</h3>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${confConfig.class}`}>
                          <div className={`w-2 h-2 rounded-full ${confConfig.dot}`} />
                          {scorecard.data_confidence}% — {confConfig.label}
                        </span>
                      </div>
                      <div className="space-y-3">
                        {[
                          { label: "Evidence Coverage", key: "evidence_coverage", weight: "35%", desc: "Verified supporting documents" },
                          { label: "Data Completeness", key: "data_completeness", weight: "25%", desc: "Fully populated activity records" },
                          { label: "Factor Reliability", key: "factor_reliability", weight: "20%", desc: "Approved factors with known source" },
                          { label: "Source Quality", key: "source_quality", weight: "20%", desc: "Factors from authoritative sources (IEA, IPCC, DEFRA)" }
                        ].map(({ label, key, weight, desc }) => {
                          const val = scorecard.confidence_components[key] || 0;
                          return (
                            <div key={key}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-slate-900">{label}</span>
                                  <span className="text-xs text-slate-400">({weight})</span>
                                </div>
                                <span className="text-sm font-bold tabular-nums text-[#059669]">{val}%</span>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#059669] rounded-full" style={{ width: `${val}%` }} />
                              </div>
                              <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Scope breakdown */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold mb-4">Emissions by Scope</h3>
                    {[
                      { label: "Scope 1 — Direct Emissions", value: (scorecard.scope_1_kgco2e || 0) / 1000 },
                      { label: "Scope 2 — Purchased Energy", value: (scorecard.scope_2_kgco2e || 0) / 1000 },
                      { label: "Scope 3 — Value Chain", value: (scorecard.scope_3_kgco2e || 0) / 1000 }
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                        <span className="text-sm text-slate-600">{label}</span>
                        <span className="text-sm font-semibold tabular-nums">{value.toFixed(2)} tCO₂e</span>
                      </div>
                    ))}
                    <div className="flex justify-between mt-3 pt-3 border-t border-slate-200">
                      <span className="text-sm font-bold">Total</span>
                      <span className="text-sm font-bold text-[#059669] tabular-nums">{scorecard.total_emissions_tco2e?.toFixed(2)} tCO₂e</span>
                    </div>
                  </div>

                  {/* Executive Insights — replaces basic AI insights */}
                  <ExecutiveInsights assessmentId={parseInt(id)} scorecard={scorecard} />
                </>
              )}
            </TabsContent>

            {/* BASELINE SUMMARY */}
            <TabsContent value="baseline" className="mt-0">
              <BaselineSummary assessmentId={parseInt(id)} />
            </TabsContent>

            {/* ENERGY MIX (Nigeria/Country) */}
            <TabsContent value="energy-mix" className="mt-0">
              <SectionCard>

                <div className="flex items-start justify-between mb-4">
                  <p className="text-xs text-slate-400 leading-relaxed max-w-xl">
                    Country-specific energy source breakdown (grid/diesel/solar). Extends V1 with
                    local energy context, Nigeria scenarios, and diesel dependency advisory.
                    V1 calculation results are unchanged.
                  </p>
                  <a href={`/calculator?assessment=${id}`} target="_blank" rel="noreferrer"
                     className="ml-4 flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium rounded-lg hover:bg-amber-100 transition-colors"
                     data-testid="open-calculator-link">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                    Solar ROI Calculator
                  </a>
                </div>
                <NigeriaEnergyModule
                  assessmentId={parseInt(id)}
                  reportingYear={assessment?.reporting_year}
                />
                        </SectionCard>
            </TabsContent>

            {/* INTELLIGENCE */}
            <TabsContent value="intelligence" className="mt-0">
              <IntelligencePanel assessmentId={parseInt(id)} />
            </TabsContent>

            {/* INVESTMENT PACK */}
            <TabsContent value="investment-pack" className="mt-0">
              <InvestmentPackPanel assessmentId={parseInt(id)} />
            </TabsContent>

            {/* REPORTS */}
            <TabsContent value="reports" className="mt-0">
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold">Reports & Passport</h3>
                  <button onClick={() => navigate("/reports")} className="text-xs text-[#059669] hover:underline">Manage Reports</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: "View Climate Passport", desc: "Preview credential page", action: () => navigate(`/passport/${id}`), color: "bg-[#0F172A] text-white hover:bg-[#1E293B]" },
                    { label: "Generate Report", desc: "Create assessment report", action: () => navigate("/reports"), color: "border border-slate-300 text-slate-700 hover:bg-slate-50" }
                  ].map(({ label, desc, action, color }) => (
                    <button key={label} onClick={action} className={`p-4 rounded-xl text-left transition-colors ${color}`}>
                      <p className="text-sm font-semibold">{label}</p>
                      <p className={`text-xs mt-0.5 ${color.includes('[#0F172A]') ? "text-slate-400" : "text-slate-500"}`}>{desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
